import React, { useState } from 'react';
import { Gauge } from 'lucide-react';

const CustomerValueScore = ({ customerEmail, cartValue, platform }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inputEmail, setInputEmail] = useState(customerEmail || '');
  const [inputCartValue, setInputCartValue] = useState(cartValue || '');

  const getScore = async () => {
    if (!inputEmail && !inputCartValue) {
      setError('Please enter customer email or cart value');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
          ? 'http://localhost:3003' 
          : 'https://cartresq-yiq2.onrender.com');
      
      const response = await fetch(`${apiBaseUrl}/api/ai/customer-value`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customerEmail: inputEmail || undefined,
          cartValue: inputCartValue ? parseFloat(inputCartValue) : undefined,
          platform
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && typeof data.score === 'number') {
        setResult(data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Customer value score error:', err);
      setError(err.message || 'Failed to get customer value score');
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'high':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-900';
      case 'low':
        return 'bg-red-50 border-red-200 text-red-900';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-900';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Gauge className="w-5 h-5 text-purple-600" />
        <h3 className="text-sm font-medium text-gray-900">AI Customer Value Score</h3>
      </div>

      <div className="space-y-2 mb-3">
        <div>
          <label className="block text-xs text-gray-700 mb-1">Customer Email (optional)</label>
          <input
            type="email"
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="customer@example.com"
            value={inputEmail}
            onChange={(e) => setInputEmail(e.target.value)}
            disabled={!!customerEmail}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-700 mb-1">Cart Value (optional)</label>
          <input
            type="number"
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            placeholder="0.00"
            value={inputCartValue}
            onChange={(e) => setInputCartValue(e.target.value)}
            disabled={!!cartValue}
          />
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 mb-2 p-2 bg-red-50 rounded">
          {error}
        </div>
      )}

      <button
        onClick={getScore}
        disabled={loading || (!inputEmail && !inputCartValue)}
        className="w-full text-xs px-3 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Analyzing...' : 'Get Value Score'}
      </button>

      {result && (
        <div className={`mt-4 p-4 border rounded-md ${getTierColor(result.tier)}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold">Value Score</span>
            <span className={`text-lg font-bold ${getScoreColor(result.score)}`}>
              {result.score}/100
            </span>
          </div>
          <div className="mb-2">
            <span className="text-xs font-medium">Tier: </span>
            <span className="text-xs font-semibold uppercase">{result.tier}</span>
          </div>
          <p className="text-xs mb-2 mt-2">
            {result.recommendation}
          </p>
          {result.suggestedDiscount && (
            <div className="mt-2 pt-2 border-t border-opacity-30">
              <p className="text-xs">
                <span className="font-medium">Suggested Discount: </span>
                <span className="font-semibold">{result.suggestedDiscount}%</span>
              </p>
            </div>
          )}
          {result.analyticsUsed && (
            <p className="text-xs mt-2 italic opacity-75">
              ✓ Based on your store's analytics data
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerValueScore;

